import { useQuery } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import type { GamePassStreakHistoryResponse } from "../../types/gamePass";
import { getPrivateInstance } from "../_privateInstance";
import { retryOnTransientErrors } from "./queryUtils";

const buildStreakHistoryUrl = (userId: string, taskId: string): string =>
  API_ROUTES.GET_GAME_PASS_USER_STREAK_HISTORY.replace(
    "{user_id}",
    userId,
  ).replace("{task_id}", taskId);

const fetchGamePassUserStreakHistory = async (
  userId: string,
  taskId: string,
): Promise<GamePassStreakHistoryResponse> => {
  const response =
    await getPrivateInstance().get<GamePassStreakHistoryResponse>(
      buildStreakHistoryUrl(userId, taskId),
    );
  return response.data;
};

type UseGetGamePassUserStreakHistoryOptions = {
  enabled?: boolean;
};

/**
 * `GET /gamepass/v2/admin/users/{user_id}/tasks/{task_id}/streak-history`
 * — PAM forensic login-streak days (FE-2664). Player contract is unchanged.
 */
export const useGetGamePassUserStreakHistory = (
  userId?: string,
  taskId?: string,
  options?: UseGetGamePassUserStreakHistoryOptions,
): UseQueryResult<GamePassStreakHistoryResponse, Error> =>
  useQuery<GamePassStreakHistoryResponse, Error>({
    queryKey: [
      "game-pass",
      "users",
      "streak-history",
      userId ?? "",
      taskId ?? "",
    ],
    queryFn: () => fetchGamePassUserStreakHistory(userId!, taskId!),
    enabled: Boolean(userId && taskId) && (options?.enabled ?? true),
    staleTime: 1000 * 30,
    refetchOnWindowFocus: false,
    retry: retryOnTransientErrors,
  });

export { buildStreakHistoryUrl, fetchGamePassUserStreakHistory };
